import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type CompanyInviteKind = "client" | "manager" | "judge";

export type CompanyInviteRow = {
  id: string;
  email: string;
  kind: CompanyInviteKind;
  role: string | null;
  company_id: string | null;
  region_id: string | null;
  status: "pending" | "accepted" | "revoked" | "expired";
  expires_at: string;
  created_at: string;
  invited_by: string;
  token: string;
};

export type InviteTeammateInput = {
  email: string;
  companyId: string;
  companyName: string;
  kind: CompanyInviteKind;
  regionId?: string | null;
  message?: string;
  inviterName?: string;
};

export type InviteTeammateResult =
  | { mode: "new_user" | "existing_user"; user_id?: string }
  | { mode: "error"; error: string };

/**
 * Send a teammate invite. Goes through the `invite-company-user` Edge
 * Function which (a) handles new-vs-existing user branching, (b) creates
 * the auth user + magic link if new, (c) ensures company_users + user_roles
 * + recruiting_team_managers rows, and (d) sends a branded Resend email.
 *
 * Returns the mode so the caller can render the right success toast
 * ("Invite sent to …" vs "{name} added to {company}").
 */
export async function inviteTeammate(input: InviteTeammateInput): Promise<InviteTeammateResult> {
  const email = input.email.trim().toLowerCase();
  if (!email) return { mode: "error", error: "Email is required." };
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { mode: "error", error: "That email doesn't look valid." };
  }

  const { data, error } = await supabase.functions.invoke("invite-company-user", {
    body: {
      kind: input.kind,
      role: input.kind,
      email,
      company_id: input.companyId,
      company_name: input.companyName,
      region_id: input.regionId ?? null,
      message: input.message ?? "",
      inviter_name: input.inviterName ?? "",
    },
  });

  if (error) {
    return { mode: "error", error: error.message };
  }
  const payload = (data ?? {}) as { mode?: string; user_id?: string; error?: string };
  if (payload.error) return { mode: "error", error: payload.error };

  await logAdminAction({
    action: "company_member.invite",
    entityType: "invites",
    entityId: null,
    summary: `Invited ${email} to ${input.companyName} as ${input.kind}`,
    diff: {
      after: { email, kind: input.kind, region_id: input.regionId ?? null, company_id: input.companyId },
    },
  });

  if (payload.mode === "existing_user") return { mode: "existing_user", user_id: payload.user_id };
  return { mode: "new_user", user_id: payload.user_id };
}

/** Pending invites for a company — surfaced as rows in the Members table
 *  with a "pending" pill so the inviter can see who hasn't accepted yet. */
export async function listPendingCompanyInvites(companyId: string): Promise<CompanyInviteRow[]> {
  const { data, error } = await supabase
    .from("invites")
    .select("*")
    .eq("company_id", companyId)
    .eq("status", "pending")
    .in("kind", ["client", "manager", "judge"])
    .order("created_at", { ascending: false });
  if (error || !data) return [];
  return data as CompanyInviteRow[];
}

/** Re-sends the invite email and bumps expires_at +7 days. Calls the
 *  Edge Function again so the existing-user branch will land the
 *  teammate in company_users immediately. */
export async function resendCompanyInvite(
  invite: CompanyInviteRow,
  companyName: string,
  inviterName?: string,
): Promise<{ error: Error | null }> {
  const { error: bumpErr } = await supabase
    .from("invites")
    .update({ expires_at: new Date(Date.now() + 7 * 86400 * 1000).toISOString() })
    .eq("id", invite.id);
  if (bumpErr) return { error: new Error(bumpErr.message) };

  const { error } = await supabase.functions.invoke("invite-company-user", {
    body: {
      kind: invite.kind,
      role: invite.role ?? invite.kind,
      email: invite.email,
      company_id: invite.company_id,
      company_name: companyName,
      region_id: invite.region_id,
      message: "",
      inviter_name: inviterName ?? "",
    },
  });
  return { error: error ? new Error(error.message) : null };
}

export async function revokeCompanyInvite(inviteId: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("invites").update({ status: "revoked" }).eq("id", inviteId);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "company_member.invite_revoke",
    entityType: "invites",
    entityId: inviteId,
    summary: `Revoked teammate invite ${inviteId.slice(0, 8)}…`,
  });
  return { error: null };
}
